/**
 * Grauv cart celebrations.
 *
 * - A congratulations popup (with a little confetti) when the offer code is applied or the free gift unlocks.
 * - A "price drop": the cart total counts down to its new, lower value.
 *
 * Texts come from Theme settings > Cart offer banner (read from [data-grauv-celebrate-config]).
 * The offer banner calls window.grauvCelebrate({ type: 'discount' | 'gift', before?: string }).
 */

const ICONS = {
  spark:
    '<svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor" aria-hidden="true"><path d="M12 2l1.9 5.6L19.5 9.5l-5.6 1.9L12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2zm7 11l.9 2.6 2.6.9-2.6.9L19 20l-.9-2.6-2.6-.9 2.6-.9L19 13z"/></svg>',
  close:
    '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>',
};

/** An animated gift box: the lid lifts off and sparkles burst out. */
const GIFT_OPEN =
  '<svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><g class="grauv-gift__base"><rect x="5" y="11.5" width="14" height="9.5" rx="1"/><path d="M12 11.5V21"/></g><g class="grauv-gift__lid"><rect x="3.5" y="7.5" width="17" height="4" rx="1"/><path d="M12 7.5V11.5M12 7.5C10 7.5 7.8 6.8 7.8 5.2c0-1 .8-1.7 1.7-1.7C11 3.5 12 5.6 12 7.5zM12 7.5c2 0 4.2-.7 4.2-2.3 0-1-.8-1.7-1.7-1.7C13 3.5 12 5.6 12 7.5z"/></g></svg><i class="grauv-spark grauv-spark--1"></i><i class="grauv-spark grauv-spark--2"></i><i class="grauv-spark grauv-spark--3"></i><i class="grauv-spark grauv-spark--4"></i>';

const COLORS = ['#b89b72', '#e3c999', '#f5f1e8', '#8a6f4e', '#d4b483'];

/** @type {Array<{ type: string; before?: string; delay?: number }>} */
const queue = [];
let playing = false;
let watching = false;

const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const config = () => document.querySelector('[data-grauv-celebrate-config]');

/**
 * @param {string} key
 * @param {string} fallback
 */
const setting = (key, fallback) => {
  const value = config()?.dataset?.[key];
  return value ? value : fallback;
};

const dialog = () => document.querySelector('#cart-drawer dialog');
const isOpen = () => {
  const d = dialog();
  return d instanceof HTMLDialogElement ? d.open : true;
};
const host = () => {
  const d = dialog();
  return d instanceof HTMLDialogElement && d.open ? d : document.body;
};

/** @param {string} value */
function parseMoney(value) {
  const match = /^(\D*)([\d,]+(?:\.\d+)?)(\D*)$/.exec((value || '').trim());
  if (!match || /,\d{1,2}$/.test(match[2])) return null;
  const number = match[2];
  return {
    prefix: match[1],
    suffix: match[3],
    value: Number(number.replace(/,/g, '')),
    decimals: (number.split('.')[1] || '').length,
  };
}

/**
 * @param {{ prefix: string; suffix: string; decimals: number }} shape
 * @param {number} amount
 */
function formatMoney(shape, amount) {
  return (
    shape.prefix +
    amount.toLocaleString('en-US', { minimumFractionDigits: shape.decimals, maximumFractionDigits: shape.decimals }) +
    shape.suffix
  );
}

/** @param {Element} node */
function leafOf(node) {
  if (!node.children.length) return node;
  return [...node.querySelectorAll('*')].find((n) => !n.children.length && /\d/.test(n.textContent || '')) || node;
}

/**
 * Counts the cart total down from its old value to the current one.
 * @param {Element} node
 * @param {string} before
 */
function dropPrice(node, before) {
  const leaf = leafOf(node);
  const finalText = (leaf.textContent || '').trim();
  const from = parseMoney(before);
  const to = parseMoney(finalText);
  if (!from || !to || from.value <= to.value || reducedMotion()) return;

  const start = performance.now();
  const duration = 1300;
  leaf.textContent = formatMoney(to, from.value);

  /** @param {number} now */
  const step = (now) => {
    const progress = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - progress, 3);
    leaf.textContent = formatMoney(to, from.value + (to.value - from.value) * eased);
    if (progress < 1) requestAnimationFrame(step);
    else leaf.textContent = finalText;
  };
  requestAnimationFrame(step);

  if (leaf instanceof HTMLElement) {
    leaf.style.transformOrigin = 'right center';
    leaf.style.display = 'inline-block';
    leaf.animate(
      [
        { color: '#b89b72', transform: 'scale(1.08)' },
        { color: 'inherit', transform: 'scale(1)' },
      ],
      { duration: 1500, easing: 'ease-out' }
    );
  }
  document.querySelectorAll('.cart-discounts__item').forEach((row) => {
    row.animate([{ backgroundColor: 'rgb(184 155 114 / 0.3)' }, { backgroundColor: 'rgb(184 155 114 / 0)' }], {
      duration: 1800,
      easing: 'ease-out',
    });
  });
}

/**
 * @param {string} before
 * @param {Element | null} node
 */
function savedAmount(before, node) {
  const fromBanner = document.querySelector('.grauv-offer__saved strong')?.textContent?.trim();
  if (fromBanner) return fromBanner;
  const b = parseMoney(before);
  const a = parseMoney(node ? leafOf(node).textContent || '' : '');
  return b && a && b.value > a.value ? formatMoney(a, b.value - a.value) : '';
}

/**
 * @param {HTMLElement} container
 * @param {number} count
 * @param {boolean} page
 */
function confetti(container, count, page) {
  if (setting('confetti', 'true') === 'false' || reducedMotion()) return;
  const layer = document.createElement('div');
  layer.className = `grauv-confetti${page ? ' grauv-confetti--page' : ''}`;
  container.append(layer);
  const width = page ? window.innerWidth : container.clientWidth;
  const height = page ? window.innerHeight : container.clientHeight;
  const originX = width / 2;
  const originY = 56;

  for (let i = 0; i < count; i++) {
    const piece = document.createElement('i');
    const w = 5 + Math.random() * 5;
    const h = 7 + Math.random() * 7;
    piece.style.cssText = `left:${originX}px;top:${originY}px;width:${w}px;height:${h}px;background:${
      COLORS[i % COLORS.length]
    };border-radius:${Math.random() < 0.4 ? '50%' : '1px'}`;
    layer.append(piece);
    const dx = (Math.random() - 0.5) * width * 0.95;
    const dy = height * (0.3 + Math.random() * 0.55);
    const spin = Math.random() * 720 - 360;
    piece.animate(
      [
        { transform: 'translate(0,0) rotate(0deg)', opacity: 1 },
        {
          transform: `translate(${dx * 0.55}px,${-30 - Math.random() * 70}px) rotate(${spin * 0.4}deg)`,
          opacity: 1,
          offset: 0.28,
        },
        { transform: `translate(${dx}px,${dy}px) rotate(${spin}deg)`, opacity: 0 },
      ],
      { duration: 1500 + Math.random() * 900, easing: 'cubic-bezier(.2,.7,.4,1)', fill: 'forwards' }
    );
  }
  setTimeout(() => layer.remove(), 2900);
}

/**
 * @param {'discount' | 'gift'} kind
 * @param {string} saved
 * @returns {Promise<void>}
 */
function popup(kind, saved) {
  const seconds = Math.min(10, Math.max(3, parseInt(setting('seconds', '5'), 10) || 5));
  const title = setting('title', 'Congratulations!');
  const template =
    kind === 'gift'
      ? setting('giftText', "You've unlocked a FREE surprise gift!")
      : setting('discountText', 'Extra 15% OFF applied. You saved {amount}.');
  const message = (
    saved ? template.replace('{amount}', saved) : template.replace(/[^.!?]*\{amount\}[^.!?]*[.!?]?\s*/g, '')
  ).trim();

  const container = host();
  const page = container === document.body;
  const el = document.createElement('div');
  el.className = `grauv-celebrate grauv-celebrate--${kind}${page ? ' grauv-celebrate--page' : ''}`;
  el.setAttribute('role', 'status');
  el.style.setProperty('--grauv-seconds', `${seconds}s`);
  el.innerHTML = `<span class="grauv-celebrate__icon">${kind === 'gift' ? GIFT_OPEN : ICONS.spark}</span>
    <div class="grauv-celebrate__text">${kind === 'gift' ? '<span class="grauv-celebrate__tag"></span>' : ''}<p class="grauv-celebrate__title"></p><p class="grauv-celebrate__msg"></p></div>
    <button type="button" class="grauv-celebrate__close" aria-label="Close">${ICONS.close}</button>
    <span class="grauv-celebrate__timer"></span>`;
  const titleEl = el.querySelector('.grauv-celebrate__title');
  const messageEl = el.querySelector('.grauv-celebrate__msg');
  const tagEl = el.querySelector('.grauv-celebrate__tag');
  if (tagEl) tagEl.textContent = setting('giftTag', 'FREE GIFT');
  if (titleEl) titleEl.textContent = title;
  if (messageEl) messageEl.textContent = message;
  container.append(el);
  confetti(container, kind === 'gift' ? 52 : 24, page);
  if (kind === 'gift') setTimeout(() => confetti(container, 28, page), 450);

  return new Promise((resolve) => {
    let done = false;
    /** @type {MutationObserver | null} */
    let observer = null;
    const timer = setTimeout(() => finish(true), seconds * 1000);

    /** @param {boolean} animate */
    function finish(animate) {
      if (done) return;
      done = true;
      clearTimeout(timer);
      observer?.disconnect();
      if (animate) {
        el.classList.add('is-leaving');
        setTimeout(() => {
          el.remove();
          resolve();
        }, 320);
      } else {
        el.remove();
        resolve();
      }
    }

    el.querySelector('.grauv-celebrate__close')?.addEventListener('click', () => finish(true));
    const d = dialog();
    if (d && !page) {
      observer = new MutationObserver(() => {
        if (!(d instanceof HTMLDialogElement) || !d.open) finish(false);
      });
      observer.observe(d, { attributes: true, attributeFilter: ['open'] });
    }
  });
}

/** @param {{ type: string; before?: string }} item */
async function run(item) {
  const enabled = config()?.dataset.enabled !== 'false';

  if (item.type === 'discount') {
    const total = document.querySelector('.cart-totals__total-value');
    const saved = savedAmount(item.before || '', total);
    if (total && item.before) dropPrice(total, item.before);
    if (enabled) await popup('discount', saved);
  } else if (item.type === 'gift') {
    document.querySelectorAll('.grauv-offer__ticket').forEach((ticket) => {
      ticket.classList.remove('is-pop');
      void (/** @type {HTMLElement} */ (ticket).offsetWidth);
      ticket.classList.add('is-pop');
    });
    if (enabled) await popup('gift', '');
  }
}

function schedule() {
  if (playing || !queue.length || !isOpen()) return;
  playing = true;
  const item = queue.shift();
  if (!item) return;
  setTimeout(async () => {
    try {
      await run(item);
    } catch (e) {
      /* a celebration must never break the cart */
    }
    playing = false;
    setTimeout(schedule, 400);
  }, item.delay ?? 500);
}

function watch() {
  if (watching) return;
  const d = dialog();
  if (!d) return;
  watching = true;
  new MutationObserver(() => schedule()).observe(d, { attributes: true, attributeFilter: ['open'] });
}

/** @param {{ type: string; before?: string }} item */
function enqueue(item) {
  if (!item || queue.some((queued) => queued.type === item.type)) return;
  queue.push(item);
  watch();
  schedule();
}

// @ts-ignore - exposed for the offer banner
window.grauvCelebrate = enqueue;
// @ts-ignore - drain anything the banner queued before this module loaded
(window.__grauvCelebrateQueue || []).splice(0).forEach(enqueue);